import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// ============================================
// Tipos
// ============================================

interface PlanLimits {
  eventos: string;
  bilhetes: string;
  membros: string;
  comissao: string;
}

interface Plan {
  key: string;
  label: string;
  tagline: string;
  badge: string | null;
  highlight: boolean;
  priceLine: string;
  cta: string;
  ctaHref: string;
  limits: PlanLimits;
}

interface FeatureGroup {
  id: number;
  group: string;
}

interface FeatureRow {
  id: number;
  label: string;
  tip?: string;
  values: {
    starter: boolean | string;
    pro: boolean | string;
    business: boolean | string;
    enterprise: boolean | string;
  };
}

type Feature = FeatureGroup | FeatureRow;

function isGroup(f: Feature): f is FeatureGroup {
  return 'group' in f;
}

interface FaqItem {
  id: number;
  q: string;
  a: string;
}

type PlanKey = 'starter' | 'pro' | 'business' | 'enterprise';
const PLAN_KEYS: PlanKey[] = ['starter', 'pro', 'business', 'enterprise'];

// ============================================
// Componente principal
// ============================================

const PlanosConfig: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Dados
  const [plans, setPlans] = useState<Plan[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [faq, setFaq] = useState<FaqItem[]>([]);

  // Dialog estados
  const [deleteFeatureDialog, setDeleteFeatureDialog] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [addFeatureDialog, setAddFeatureDialog] = useState(false);
  const [addGroupDialog, setAddGroupDialog] = useState(false);

  // Novos itens
  const [newGroup, setNewGroup] = useState('');
  const [newFeature, setNewFeature] = useState<{
    label: string;
    tip: string;
    group: string;
    values: Record<PlanKey, string | boolean>;
  }>({
    label: '',
    tip: '',
    group: '',
    values: { starter: false, pro: false, business: false, enterprise: false },
  });

  // ============================================
  // Fetch inicial
  // ============================================

  const fetchData = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await api.get('/planos-cms');
      setPlans(res.data.plans ?? []);
      setFeatures(res.data.features ?? []);
      setFaq(res.data.faq ?? []);
    } catch {
      setErrorMsg('Erro ao carregar dados. Verifica a tua ligação e tenta de novo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ============================================
  // Guardar
  // ============================================

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setErrorMsg('');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg('');
    try {
      if (tab === 0) {
        await api.put('/planos-cms/plans', { valor: plans });
        showSuccess('Planos guardados com sucesso.');
      } else if (tab === 1) {
        await api.put('/planos-cms/features', { valor: features });
        showSuccess('Funcionalidades guardadas com sucesso.');
      } else {
        await api.put('/planos-cms/faq', { valor: faq });
        showSuccess('FAQ guardado com sucesso.');
      }
    } catch {
      setErrorMsg('Erro ao guardar. Tenta de novo.');
    } finally {
      setSaving(false);
    }
  };

  // ============================================
  // Helpers genéricos
  // ============================================

  const moveItem = <T,>(arr: T[], idx: number, dir: -1 | 1): T[] => {
    const next = idx + dir;
    if (next < 0 || next >= arr.length) return arr;
    const copy = [...arr];
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    return copy;
  };

  // ============================================
  // Tab Planos — helpers
  // ============================================

  const updatePlan = (idx: number, field: keyof Plan, value: unknown) => {
    setPlans((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };

  const updatePlanLimit = (idx: number, limitKey: keyof PlanLimits, value: string) => {
    setPlans((prev) =>
      prev.map((p, i) =>
        i === idx ? { ...p, limits: { ...p.limits, [limitKey]: value } } : p,
      ),
    );
  };

  // ============================================
  // Tab Features — helpers
  // ============================================

  const updateFeatureField = (id: number, field: string, value: unknown) => {
    setFeatures((prev) =>
      prev.map((f) => {
        if (f.id !== id || isGroup(f)) return f;
        return { ...f, [field]: value } as FeatureRow;
      }),
    );
  };

  const updateFeatureValue = (id: number, planKey: PlanKey, value: boolean | string) => {
    setFeatures((prev) =>
      prev.map((f) => {
        if (f.id !== id || isGroup(f)) return f;
        return { ...f, values: { ...(f as FeatureRow).values, [planKey]: value } } as FeatureRow;
      }),
    );
  };

  const deleteFeature = (id: number) => {
    setFeatures((prev) => prev.filter((f) => f.id !== id));
    setDeleteFeatureDialog({ open: false, id: null });
  };

  const moveFeature = (idx: number, dir: -1 | 1) => {
    setFeatures((prev) => moveItem(prev, idx, dir));
  };

  const getGroupNames = (): string[] => {
    return features
      .filter(isGroup)
      .map((f) => (f as FeatureGroup).group);
  };

  const addGroup = () => {
    if (!newGroup.trim()) return;
    const maxId = features.reduce((m, f) => Math.max(m, f.id), 0);
    setFeatures((prev) => [...prev, { id: maxId + 1, group: newGroup.trim() }]);
    setNewGroup('');
    setAddGroupDialog(false);
  };

  const addFeature = () => {
    if (!newFeature.label.trim()) return;
    const maxId = features.reduce((m, f) => Math.max(m, f.id), 0);
    const newItem: FeatureRow = {
      id: maxId + 1,
      label: newFeature.label.trim(),
      tip: newFeature.tip.trim() || undefined,
      values: {
        starter:    newFeature.values.starter,
        pro:        newFeature.values.pro,
        business:   newFeature.values.business,
        enterprise: newFeature.values.enterprise,
      },
    };

    // Inserir depois do grupo escolhido (ou no final)
    if (newFeature.group) {
      const groupIdx = features.findIndex((f) => isGroup(f) && (f as FeatureGroup).group === newFeature.group);
      if (groupIdx >= 0) {
        const copy = [...features];
        copy.splice(groupIdx + 1, 0, newItem);
        setFeatures(copy);
      } else {
        setFeatures((prev) => [...prev, newItem]);
      }
    } else {
      setFeatures((prev) => [...prev, newItem]);
    }

    setNewFeature({ label: '', tip: '', group: '', values: { starter: false, pro: false, business: false, enterprise: false } });
    setAddFeatureDialog(false);
  };

  // ============================================
  // Tab FAQ — helpers
  // ============================================

  const updateFaq = (id: number, field: 'q' | 'a', value: string) => {
    setFaq((prev) => prev.map((f) => (f.id === id ? { ...f, [field]: value } : f)));
  };

  const deleteFaq = (id: number) => {
    setFaq((prev) => prev.filter((f) => f.id !== id));
  };

  const moveFaq = (idx: number, dir: -1 | 1) => {
    setFaq((prev) => moveItem(prev, idx, dir));
  };

  const addFaqItem = () => {
    const maxId = faq.reduce((m, f) => Math.max(m, f.id), 0);
    setFaq((prev) => [...prev, { id: maxId + 1, q: '', a: '' }]);
  };

  // ============================================
  // Render valor de feature (célula da tabela)
  // ============================================

  const renderFeatureValueCell = (feature: FeatureRow, planKey: PlanKey) => {
    const val = feature.values[planKey];
    if (typeof val === 'boolean') {
      return (
        <Checkbox
          checked={val}
          size="small"
          onChange={(e) => updateFeatureValue(feature.id, planKey, e.target.checked)}
        />
      );
    }
    return (
      <TextField
        value={val}
        size="small"
        variant="standard"
        inputProps={{ style: { fontSize: 12, width: 80 } }}
        onChange={(e) => updateFeatureValue(feature.id, planKey, e.target.value)}
      />
    );
  };

  // ============================================
  // Loading
  // ============================================

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  // ============================================
  // Render
  // ============================================

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Planos events.cv
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gere o conteúdo da página /planos
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          Guardar alterações
        </Button>
      </Box>

      {/* Alertas */}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
      {errorMsg   && <Alert severity="error"   sx={{ mb: 2 }}>{errorMsg}</Alert>}

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Planos" />
        <Tab label="Funcionalidades" />
        <Tab label="FAQ" />
      </Tabs>

      {/* ──────────────── TAB 0: PLANOS ──────────────── */}
      {tab === 0 && (
        <Grid container spacing={3}>
          {plans.map((plan, idx) => (
            <Grid item xs={12} sm={6} key={plan.key}>
              <Card variant="outlined" sx={plan.highlight ? { borderColor: 'primary.main', borderWidth: 2 } : {}}>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={plan.label} size="small" color={plan.highlight ? 'primary' : 'default'} />
                      <Chip label={plan.key} size="small" variant="outlined" />
                    </Box>
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Tagline"
                        value={plan.tagline}
                        fullWidth
                        size="small"
                        onChange={(e) => updatePlan(idx, 'tagline', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Badge (opcional)"
                        value={plan.badge ?? ''}
                        fullWidth
                        size="small"
                        onChange={(e) => updatePlan(idx, 'badge', e.target.value || null)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Preço"
                        value={plan.priceLine}
                        fullWidth
                        size="small"
                        onChange={(e) => updatePlan(idx, 'priceLine', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Texto do botão"
                        value={plan.cta}
                        fullWidth
                        size="small"
                        onChange={(e) => updatePlan(idx, 'cta', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Link do botão"
                        value={plan.ctaHref}
                        fullWidth
                        size="small"
                        onChange={(e) => updatePlan(idx, 'ctaHref', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={plan.highlight}
                            onChange={(e) => updatePlan(idx, 'highlight', e.target.checked)}
                          />
                        }
                        label="Destacar"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}>
                        <Typography variant="caption" color="text.secondary">Limites</Typography>
                      </Divider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Eventos"
                        value={plan.limits.eventos}
                        fullWidth
                        size="small"
                        onChange={(e) => updatePlanLimit(idx, 'eventos', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Bilhetes"
                        value={plan.limits.bilhetes}
                        fullWidth
                        size="small"
                        onChange={(e) => updatePlanLimit(idx, 'bilhetes', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Membros"
                        value={plan.limits.membros}
                        fullWidth
                        size="small"
                        onChange={(e) => updatePlanLimit(idx, 'membros', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Comissão"
                        value={plan.limits.comissao}
                        fullWidth
                        size="small"
                        onChange={(e) => updatePlanLimit(idx, 'comissao', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ──────────────── TAB 1: FUNCIONALIDADES ──────────────── */}
      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setAddFeatureDialog(true)}>
              Adicionar funcionalidade
            </Button>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setAddGroupDialog(true)}>
              Adicionar grupo
            </Button>
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 220 }}>Funcionalidade</TableCell>
                  <TableCell align="center">Starter</TableCell>
                  <TableCell align="center">Pro</TableCell>
                  <TableCell align="center">Business</TableCell>
                  <TableCell align="center">Enterprise</TableCell>
                  <TableCell align="center" sx={{ width: 120 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {features.map((feature, idx) => {
                  if (isGroup(feature)) {
                    return (
                      <TableRow key={feature.id} sx={{ bgcolor: 'action.hover' }}>
                        <TableCell
                          colSpan={5}
                          sx={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}
                        >
                          {feature.group}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Mover para cima">
                            <span>
                              <IconButton size="small" onClick={() => moveFeature(idx, -1)} disabled={idx === 0}>
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Mover para baixo">
                            <span>
                              <IconButton size="small" onClick={() => moveFeature(idx, 1)} disabled={idx === features.length - 1}>
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Eliminar grupo">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteFeatureDialog({ open: true, id: feature.id })}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  const f = feature as FeatureRow;
                  return (
                    <TableRow key={f.id} hover>
                      <TableCell>
                        <TextField
                          value={f.label}
                          size="small"
                          variant="standard"
                          fullWidth
                          inputProps={{ style: { fontSize: 13 } }}
                          onChange={(e) => updateFeatureField(f.id, 'label', e.target.value)}
                        />
                        <TextField
                          value={f.tip ?? ''}
                          size="small"
                          variant="standard"
                          fullWidth
                          placeholder="Tooltip (opcional)"
                          inputProps={{ style: { fontSize: 11, color: '#888' } }}
                          onChange={(e) => updateFeatureField(f.id, 'tip', e.target.value || undefined)}
                        />
                      </TableCell>
                      {PLAN_KEYS.map((pk) => (
                        <TableCell key={pk} align="center">
                          {renderFeatureValueCell(f, pk)}
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <Tooltip title="Mover para cima">
                          <span>
                            <IconButton size="small" onClick={() => moveFeature(idx, -1)} disabled={idx === 0}>
                              <ArrowUpIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Mover para baixo">
                          <span>
                            <IconButton size="small" onClick={() => moveFeature(idx, 1)} disabled={idx === features.length - 1}>
                              <ArrowDownIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteFeatureDialog({ open: true, id: f.id })}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}

      {/* ──────────────── TAB 2: FAQ ──────────────── */}
      {tab === 2 && (
        <Box>
          {faq.map((item, idx) => (
            <Card key={item.id} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="Pergunta"
                      value={item.q}
                      fullWidth
                      size="small"
                      sx={{ mb: 1 }}
                      onChange={(e) => updateFaq(item.id, 'q', e.target.value)}
                    />
                    <TextField
                      label="Resposta"
                      value={item.a}
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                      onChange={(e) => updateFaq(item.id, 'a', e.target.value)}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Tooltip title="Mover para cima">
                      <span>
                        <IconButton size="small" onClick={() => moveFaq(idx, -1)} disabled={idx === 0}>
                          <ArrowUpIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Mover para baixo">
                      <span>
                        <IconButton size="small" onClick={() => moveFaq(idx, 1)} disabled={idx === faq.length - 1}>
                          <ArrowDownIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => deleteFaq(item.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addFaqItem}>
            Adicionar pergunta
          </Button>
        </Box>
      )}

      {/* ──────────────── DIALOG: Confirmar delete feature ──────────────── */}
      <Dialog open={deleteFeatureDialog.open} onClose={() => setDeleteFeatureDialog({ open: false, id: null })}>
        <DialogTitle>Eliminar item</DialogTitle>
        <DialogContent>
          <Typography>Tens a certeza que queres eliminar este item? Esta ação é local até guardares.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFeatureDialog({ open: false, id: null })}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteFeatureDialog.id !== null && deleteFeature(deleteFeatureDialog.id)}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ──────────────── DIALOG: Adicionar grupo ──────────────── */}
      <Dialog open={addGroupDialog} onClose={() => setAddGroupDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Adicionar grupo</DialogTitle>
        <DialogContent>
          <TextField
            label="Nome do grupo"
            value={newGroup}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            onChange={(e) => setNewGroup(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addGroup()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddGroupDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={addGroup} disabled={!newGroup.trim()}>
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ──────────────── DIALOG: Adicionar funcionalidade ──────────────── */}
      <Dialog open={addFeatureDialog} onClose={() => setAddFeatureDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar funcionalidade</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                label="Label"
                value={newFeature.label}
                fullWidth
                size="small"
                required
                onChange={(e) => setNewFeature((p) => ({ ...p, label: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Tooltip (opcional)"
                value={newFeature.tip}
                fullWidth
                size="small"
                onChange={(e) => setNewFeature((p) => ({ ...p, tip: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Grupo</InputLabel>
                <Select
                  value={newFeature.group}
                  label="Grupo"
                  onChange={(e) => setNewFeature((p) => ({ ...p, group: e.target.value }))}
                >
                  <MenuItem value=""><em>Sem grupo (no final)</em></MenuItem>
                  {getGroupNames().map((g) => (
                    <MenuItem key={g} value={g}>{g}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider>
                <Typography variant="caption" color="text.secondary">Valores por plano</Typography>
              </Divider>
            </Grid>

            {PLAN_KEYS.map((pk) => {
              const val = newFeature.values[pk];
              return (
                <Grid item xs={12} sm={6} key={pk}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ width: 80, textTransform: 'capitalize' }}>{pk}</Typography>
                    {typeof val === 'boolean' ? (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={val}
                            size="small"
                            onChange={(e) => setNewFeature((p) => ({
                              ...p,
                              values: { ...p.values, [pk]: e.target.checked },
                            }))}
                          />
                        }
                        label={val ? 'Sim' : 'Não'}
                      />
                    ) : (
                      <TextField
                        value={val}
                        size="small"
                        variant="standard"
                        onChange={(e) => setNewFeature((p) => ({
                          ...p,
                          values: { ...p.values, [pk]: e.target.value },
                        }))}
                      />
                    )}
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        const current = newFeature.values[pk];
                        setNewFeature((p) => ({
                          ...p,
                          values: { ...p.values, [pk]: typeof current === 'boolean' ? '' : false },
                        }));
                      }}
                    >
                      {typeof val === 'boolean' ? 'Texto' : 'Bool'}
                    </Button>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFeatureDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={addFeature} disabled={!newFeature.label.trim()}>
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlanosConfig;
